import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#2563eb', '#f97316', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#eab308', '#ec4899'];

const formatCurrency = (v) => `PHP ${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatCompact = (v) => {
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toFixed?.(2) ?? '0';
};

const FinancialAnalytics = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '', preset: 'all' });

  const getDatePreset = (preset) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().split('T')[0];
    switch (preset) {
      case 'today': return { start: fmt(today), end: fmt(today), preset: 'today' };
      case 'week': { const s = new Date(today); s.setDate(today.getDate() - today.getDay()); return { start: fmt(s), end: fmt(today), preset: 'week' }; }
      case 'month': return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(today), preset: 'month' };
      case 'quarter': return { start: fmt(new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1)), end: fmt(today), preset: 'quarter' };
      case 'year': return { start: fmt(new Date(today.getFullYear(), 0, 1)), end: fmt(today), preset: 'year' };
      default: return { start: '', end: '', preset: 'all' };
    }
  };

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const params = dateRange.start && dateRange.end
        ? `?start_date=${dateRange.start}&end_date=${dateRange.end}`
        : '';
      const res = await axios.get(`${API}/admin/analytics${params}`);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-400 text-lg">Loading analytics...</span>
          </div>
        </div>
      </div>
    );
  }

  const s = data?.summary || {};
  const growthPositive = s.mom_growth !== null && s.mom_growth >= 0;

  return (
    <div className="min-h-screen bg-gray-950" data-testid="analytics-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between mb-8 gap-4">
          <div>
            <button
              onClick={() => navigate('/admin')}
              className="text-sm text-gray-500 hover:text-gray-300 mb-2 flex items-center gap-1 transition"
              data-testid="back-to-dashboard"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-white" data-testid="analytics-title">Financial Analytics</h1>
            <p className="text-gray-500 mt-1">Revenue trends, category performance & provider insights</p>
          </div>
        </div>

        {/* Date Filter */}
        <div className="bg-gray-900 border border-gray-800 p-4 rounded-xl mb-6" data-testid="analytics-date-filter">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-400">Period:</span>
            <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
              {[
                { key: 'all', label: 'All Time' },
                { key: 'month', label: 'This Month' },
                { key: 'quarter', label: 'Quarter' },
                { key: 'year', label: 'This Year' },
              ].map(p => (
                <button
                  key={p.key}
                  onClick={() => setDateRange(getDatePreset(p.key))}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                    dateRange.preset === p.key
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                  data-testid={`analytics-filter-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <input type="date" value={dateRange.start}
                onChange={(e) => setDateRange({ start: e.target.value, end: dateRange.end || e.target.value, preset: 'custom' })}
                className="bg-gray-800 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-gray-300"
                data-testid="analytics-start-date"
              />
              <span className="text-gray-600 text-xs">to</span>
              <input type="date" value={dateRange.end}
                onChange={(e) => setDateRange({ start: dateRange.start || e.target.value, end: e.target.value, preset: 'custom' })}
                className="bg-gray-800 border border-gray-700 rounded-md px-2 py-1.5 text-xs text-gray-300"
                data-testid="analytics-end-date"
              />
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="kpi-cards">
          <KpiCard label="Platform Revenue" value={formatCurrency(s.total_revenue)} accent="blue" />
          <KpiCard label="Transaction Volume" value={formatCurrency(s.total_volume)} accent="gray" />
          <KpiCard label="Completed Bookings" value={s.total_bookings} accent="green" />
          <KpiCard label="Avg Revenue / Booking" value={formatCurrency(s.avg_revenue_per_booking)} accent="orange" />
          <KpiCard label="Completion Rate" value={`${s.completion_rate}%`} accent="cyan" />
          <KpiCard
            label="MoM Growth"
            value={s.mom_growth !== null ? `${s.mom_growth > 0 ? '+' : ''}${s.mom_growth}%` : 'N/A'}
            accent={growthPositive ? 'green' : 'red'}
            data-testid="mom-growth-card"
          />
        </div>

        {/* Row 1: Revenue Trend + Monthly Bars */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Revenue Trend Line */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="revenue-trend-chart">
            <h3 className="text-white font-semibold mb-4">Revenue Trend</h3>
            {data?.revenue_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={(v) => v.substring(5)} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={formatCompact} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#9ca3af' }}
                    formatter={(v) => [formatCurrency(v), 'Revenue']}
                  />
                  <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} dot={{ fill: '#2563eb', r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-gray-500 text-center py-16">No data for selected period</p>}
          </div>

          {/* Monthly Revenue Bars */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="monthly-revenue-chart">
            <h3 className="text-white font-semibold mb-4">Monthly Revenue</h3>
            {data?.monthly_revenue?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.monthly_revenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={formatCompact} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#9ca3af' }}
                    formatter={(v) => [formatCurrency(v), 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-gray-500 text-center py-16">No data for selected period</p>}
          </div>
        </div>

        {/* Row 2: Categories Pie + Categories Bar */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Categories Pie */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="categories-pie-chart">
            <h3 className="text-white font-semibold mb-4">Revenue by Category</h3>
            {data?.top_categories?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={data.top_categories}
                    cx="50%" cy="50%"
                    innerRadius={60} outerRadius={100}
                    dataKey="revenue"
                    nameKey="category"
                    label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#6b7280' }}
                  >
                    {data.top_categories.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    formatter={(v) => [formatCurrency(v), 'Revenue']}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-gray-500 text-center py-16">No data for selected period</p>}
          </div>

          {/* Categories Bar */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="categories-bar-chart">
            <h3 className="text-white font-semibold mb-4">Category Volume vs Revenue</h3>
            {data?.top_categories?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.top_categories} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={formatCompact} />
                  <YAxis dataKey="category" type="category" width={120} tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    formatter={(v) => [formatCurrency(v)]}
                  />
                  <Legend wrapperStyle={{ color: '#9ca3af' }} />
                  <Bar dataKey="volume" name="Volume" fill="#374151" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="revenue" name="Revenue" fill="#2563eb" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-gray-500 text-center py-16">No data</p>}
          </div>
        </div>

        {/* Row 3: Top Providers + Top Clients */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Providers */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="top-providers">
            <h3 className="text-white font-semibold mb-4">Top Providers by Earnings</h3>
            {data?.top_providers?.length > 0 ? (
              <div className="space-y-3">
                {data.top_providers.map((p, i) => {
                  const maxEarnings = data.top_providers[0].earnings;
                  return (
                    <div key={p.provider_id} className="flex items-center gap-3" data-testid={`provider-row-${i}`}>
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                        i === 1 ? 'bg-gray-400/20 text-gray-300' :
                        i === 2 ? 'bg-orange-500/20 text-orange-400' :
                        'bg-gray-800 text-gray-500'
                      }`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-200 truncate">{p.provider_name}</span>
                          <span className="text-sm font-semibold text-green-400 ml-2">{formatCurrency(p.earnings)}</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-1.5">
                          <div className="bg-green-500 h-1.5 rounded-full transition-all duration-500" style={{ width: `${(p.earnings / maxEarnings) * 100}%` }} />
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-gray-500">{p.bookings} bookings</span>
                          <span className="text-xs text-blue-400">Rev: {formatCurrency(p.revenue_generated)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-gray-500 text-center py-8">No data</p>}
          </div>

          {/* Top Clients */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-testid="top-clients">
            <h3 className="text-white font-semibold mb-4">Top Clients by Spending</h3>
            {data?.top_clients?.length > 0 ? (
              <div className="space-y-3">
                {data.top_clients.map((c, i) => {
                  const maxSpent = data.top_clients[0].total_spent;
                  return (
                    <div key={c.client_id} className="flex items-center gap-3" data-testid={`client-row-${i}`}>
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-blue-500/20 text-blue-400' :
                        i === 1 ? 'bg-gray-400/20 text-gray-300' :
                        i === 2 ? 'bg-orange-500/20 text-orange-400' :
                        'bg-gray-800 text-gray-500'
                      }`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-200 truncate">{c.client_name}</span>
                          <span className="text-sm font-semibold text-blue-400 ml-2">{formatCurrency(c.total_spent)}</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-1.5">
                          <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-500" style={{ width: `${(c.total_spent / maxSpent) * 100}%` }} />
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-gray-500">{c.bookings} bookings</span>
                          <span className="text-xs text-orange-400">Fees: {formatCurrency(c.fees_paid)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-gray-500 text-center py-8">No data</p>}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

const KpiCard = ({ label, value, accent }) => {
  const accentMap = {
    blue: 'border-blue-500 bg-blue-500/5',
    green: 'border-emerald-500 bg-emerald-500/5',
    orange: 'border-orange-500 bg-orange-500/5',
    gray: 'border-gray-600 bg-gray-800/50',
    cyan: 'border-cyan-500 bg-cyan-500/5',
    red: 'border-red-500 bg-red-500/5',
  };
  const textMap = {
    blue: 'text-blue-400',
    green: 'text-emerald-400',
    orange: 'text-orange-400',
    gray: 'text-gray-300',
    cyan: 'text-cyan-400',
    red: 'text-red-400',
  };
  return (
    <div className={`border-l-2 rounded-lg p-4 ${accentMap[accent] || accentMap.gray}`} data-testid={`kpi-${label.toLowerCase().replace(/[\s/]/g, '-')}`}>
      <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
      <p className={`text-lg font-bold ${textMap[accent] || textMap.gray}`}>{value}</p>
    </div>
  );
};

export default FinancialAnalytics;
